// client/src/pages/students/studentDashboard.jsx

import React, { useState, useEffect, useContext } from 'react';
import { useNavigate, useSearchParams, useLocation } from 'react-router-dom';
import ProfileHeader from '../../components/headers/profileHeader';
import SharedHeader from '../../components/headers/sharedHeader1';
import CustomButton from '../../components/customs/customButton';
import BackButton from '../../components/customs/backButton';
import showNotification from '../../utilities/showNotification';
import { GlobalContext } from '../../context/provider';
import useAnnouncement from '../../hooks/useAnnouncement'; 
import useAuditLog from '../../hooks/useAuditLog';
import useMessage from '../../hooks/useMessage';  
import useStudent from '../../hooks/useStudent';
import useCourse from '../../hooks/useCourse';
import { formatShortDate } from '../../utilities/dateUtils';

import '../../styles/globalstyles.css';
import './studentDashboard.css';

const StudentDashboard = () => {
  const [searchParams] = useSearchParams();
  const location = useLocation();

  useEffect(() => {
    // Check if coming from successful payment
    if (searchParams.get('payment') === 'success') {
      showNotification('🎉 Payment successful! You are now enrolled.', 'success');
      setActiveTab('my-courses'); // Switch to My Courses tab
      
      // Clear URL parameter
      window.history.replaceState({}, '', '/student/dashboard');
    }
  }, [searchParams]);

  const navigate = useNavigate();
  const { 
    authState: { authData },
    studentState 
  } = useContext(GlobalContext);
  
  const { 
    studentData, 
    getStudentByLoginId,
    enrollCourse,
    unenrollCourse,
    getCourseProgress,
    getCourseGrade,
    getPaymentHistory,      
    getSubscriptions,       
    cancelSubscription,     
    refundPayment,        
    createStripeCheckout,
    getSubscriptionInvoiceUrl,
  } = useStudent();
  
  const { courses, getAllCourses, getPublishedCourses } = useCourse();
  const { getUserActivity } = useAuditLog();

  const { getUnreadCount, unreadCount } = useMessage();  
  const { getUnreadAnnouncementCount } = useAnnouncement();  
  
  const [activeTab, setActiveTab] = useState(
    location.state?.activeTab || 'overview'
  );
  const [isLoading, setIsLoading] = useState(true);
  const [availableCourses, setAvailableCourses] = useState([]);
  const [myCourses, setMyCourses] = useState([]);
  const [selectedCourse, setSelectedCourse] = useState(null);
  const [modalVisible, setModalVisible] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [filterCategory, setFilterCategory] = useState('');
  const [error, setError] = useState(null);
  const [enrollmentLoading, setEnrollmentLoading] = useState(false);
  const [courseProgress, setCourseProgress] = useState({});
  const [courseGrades, setCourseGrades] = useState({});
  const [recentActivities, setRecentActivities] = useState([]);
  const [activitiesLoading, setActivitiesLoading] = useState(false);
  const [unreadAnnouncementCount, setUnreadAnnouncementCount] = useState(0);  

  // Payment & Subscription states
  const [paymentHistory, setPaymentHistory] = useState([]);
  const [subscriptions, setSubscriptions] = useState([]);
  const [showPaymentModal, setShowPaymentModal] = useState(false);
  const [selectedPayment, setSelectedPayment] = useState(null);
  const [showRefundModal, setShowRefundModal] = useState(false);
  const [selectedPaymentForRefund, setSelectedPaymentForRefund] = useState(null);
  const [refundReason, setRefundReason] = useState('');
  const [isRefundProcessing, setIsRefundProcessing] = useState(false);
  const [expandedSubs, setExpandedSubs] = useState(new Set());
  const toggleSubExpanded = (subId) => {
    setExpandedSubs(prev => {
        const next = new Set(prev);
        next.has(subId) ? next.delete(subId) : next.add(subId);
        return next;
    });
  };

  useEffect(() => {
    loadStudentData();
  }, []);

  // Update notification counts when they change
  useEffect(() => {
    if (!authData?.id) return;
    console.log("📊 Student notification counts:", {
      messages: unreadCount || 0,
      announcements: unreadAnnouncementCount || 0,
      total: (unreadCount || 0) + (unreadAnnouncementCount || 0)
    });
    
  }, [unreadCount, unreadAnnouncementCount, authData?.id]);

  useEffect(() => {
    if (authData?.id) {
      loadPaymentHistory();
      loadSubscriptions();
    }
  }, [authData]);

  const loadStudentData = async () => {
    setIsLoading(true);
    setError(null);

    let freshStudentData = null; // ← declare here, in outer scope

    try {
      if (authData?.id) {
        const studentResponse = await getStudentByLoginId(authData.id);

        if (studentResponse.success && studentResponse.data) {
          freshStudentData = studentResponse.data; // ← assign, don't declare
          const enrolledCourseIds = freshStudentData.courses?.map(c => c.courseId) || [];

          const progressPromises = enrolledCourseIds.map(async (courseId) => {
            const [progressRes, gradeRes] = await Promise.all([
              getCourseProgress(authData.id, courseId).catch(() => null),
              getCourseGrade(authData.id, courseId).catch(() => null)
            ]);
            return { courseId, progress: progressRes?.data || null, grade: gradeRes?.data || null };
          });

          const progressData = await Promise.all(progressPromises);
          const progressMap = {};
          const gradesMap = {};
          progressData.forEach(({ courseId, progress, grade }) => {
            progressMap[courseId] = progress;
            gradesMap[courseId] = grade;
          });
          setCourseProgress(progressMap);
          setCourseGrades(gradesMap);

          try {
            await getUnreadCount(authData.id);
          } catch (error) {
            console.error('Failed to load unread count:', error);
          }

          try {
            const unreadAnnouncementResponse = await getUnreadAnnouncementCount(authData.id);
            if (unreadAnnouncementResponse.success) {
              setUnreadAnnouncementCount(unreadAnnouncementResponse.unreadCount || 0);
            }
          } catch (error) {
            console.error('Failed to load unread announcement count:', error);
          }
        }
      }

      // Load all published courses
      const coursesResponse = await getPublishedCourses();

      if (coursesResponse.success && coursesResponse.data) {
        const allPublishedCourses = coursesResponse.data;
        setAvailableCourses(allPublishedCourses);

        // Only enrich if student data was successfully loaded
        if (freshStudentData?.courses) {
          const enrichedCourses = freshStudentData.courses.map(enrollment => {
            const fullCourse = allPublishedCourses.find(c => c.courseId === enrollment.courseId);
            return { ...enrollment, ...fullCourse };
          }).filter(course => course.title);

          setMyCourses(enrichedCourses);
        }
      }

      await loadRecentActivities();

    } catch (error) {
      console.error('Failed to load student data:', error);
      setError('Failed to load dashboard data. Please try refreshing.');
    } finally {
      setIsLoading(false);
    }
  };

  const loadRecentActivities = async () => {
    setActivitiesLoading(true);
    try {
      const response = await getUserActivity(authData.id, { limit: 10 });

      if (response.success) {
        const formattedActivities = formatActivities(response.data);
        setRecentActivities(formattedActivities);
      }
    } catch (error) {
      console.error('Failed to load activities:', error);
    } finally {
      setActivitiesLoading(false);
    }
  };

  const formatActivities = (activities) => {
    return activities.map(activity => {
      const icon = getActivityIcon(activity.action);
      const description = getDescription(activity);
      const timeAgo = getTimeAgo(new Date(activity.timestamp));

      return {
        icon,
        title: getActivityTitle(activity.action),
        description,
        timeAgo,
        timestamp: activity.timestamp
      };
    });
  };

  const getActivityIcon = (action) => {
    const iconMap = {
      'CREATE_STUDENT': '➕',
      'UPDATE_STUDENT': '📝',
      'ENROLL_COURSE': '📚',
      'UNENROLL_COURSE': '❌',
      'SUBMIT_LAB': '🧪',
      'SUBMIT_QUIZ': '📋',
      'SUBMIT_EXAM': '📄',
      'SUBMIT_CAPSTONE': '🎓',
      'START_QUIZ': '▶️',
      'START_EXAM': '▶️',
      'COMPLETE_LESSON': '✅',
    };
    return iconMap[action] || '📋';
  };

  const getActivityTitle = (action) => {
    const titleMap = {
      'CREATE_STUDENT': "Profile Created",
      'UPDATE_STUDENT': "Profile Updated",
      'ENROLL_COURSE': "Enrolled in Course",
      'UNENROLL_COURSE': "Dropped Course",
      'SUBMIT_LAB': "Lab Submitted",
      'SUBMIT_QUIZ': "Quiz Submitted",
      'SUBMIT_EXAM': "Exam Submitted",
      'SUBMIT_CAPSTONE': "Capstone Submitted",
      'START_QUIZ': "Quiz Started",
      'START_EXAM': "Exam Started",
      'COMPLETE_LESSON': "Lesson Completed",
    };
    return titleMap[action] || 'Activity';
  };

  const getDescription = (activity) => {
    // Use actionDescription if available (for file operations, etc.)
    if (activity.actionDescription) {
      return activity.actionDescription;
    }
    
    const metadata = activity.metadata || {};
    const entityId = activity.entityId || 'Unknown';
    const entityType = activity.entityType || '';
    
    // Build description from changes array
    if (activity.changes && activity.changes.length > 0) {
      const change = activity.changes[0];
      
      switch(activity.action) {
        // Student submission actions
        case 'SUBMIT_LAB':
          return change.newValue || `Submitted lab in course ${metadata.courseId || entityId}`;
        
        case 'SUBMIT_QUIZ':
          return change.newValue || `Submitted quiz in course ${metadata.courseId || entityId}`;
        
        case 'SUBMIT_EXAM':
          return change.newValue || `Submitted exam in course ${metadata.courseId || entityId}`;
        
        case 'SUBMIT_CAPSTONE':
          return change.newValue || `Submitted capstone project`;
          // Student enrollment actions
        case 'ENROLL':
          return `Enrolled in course ${change.newValue}`;
          
        case 'UNENROLL':
          return `Unenrolled from course ${change.oldValue}`;
          
        // Student cart actions
        case 'ADD_TO_CART':
          return `Added course ${change.newValue} to cart`;
          
        case 'REMOVE_FROM_CART':
          return `Removed course ${change.oldValue} from cart`;
          
        // Student payment action
        case 'PAYMENT':
          return change.newValue || `Completed payment`;
          
        // Student grade calculation
        case 'CALCULATE_GRADE':
          return change.newValue || `Grade calculated`;
          
        // Student progress
        case 'COMPLETE_LESSON':
          return change.newValue || `Completed lesson`;
          
        // Student profile actions
        case 'CREATE':
          return change.newValue || `Account created`;
          
        case 'UPDATE':
          return `Updated ${change.field}`;
          
        // Login action
        case 'LOGIN':
          return `Logged in`;
          
        // File operations
        case 'SUBMIT':
          return change.newValue || `Uploaded to ${entityType} ${entityId}`;
        
        case 'READ':
          return change.newValue || `Downloaded from ${entityType} ${entityId}`;
          
        case 'DELETE':
          return change.oldValue ? `Deleted ${change.oldValue}` : `Deleted file`;
          
        default:
          // Generic fallback
          return change.newValue || change.oldValue || `${activity.action}`;
      }
    }
    
    // Final fallback
    return `${activity.action}`;
  };

  const getTimeAgo = (date) => {
    const seconds = Math.floor((new Date() - date) / 1000);
    
    const intervals = {
      year: 31536000,
      month: 2592000,
      week: 604800,
      day: 86400,
      hour: 3600,
      minute: 60
    };

    for (const [unit, secondsInUnit] of Object.entries(intervals)) {
      const interval = Math.floor(seconds / secondsInUnit);
      if (interval >= 1) {
        return `${interval} ${unit}${interval > 1 ? 's' : ''} ago`;
      }
    }
    
    return 'just now';
  };

  const filteredCourses = availableCourses.filter(course => {
    const matchesSearch = !searchQuery || 
      (course.title && course.title.toLowerCase().includes(searchQuery.toLowerCase()));
    const matchesFilter = !filterCategory || course.category === filterCategory;
    
    // Don't show courses student is already enrolled in
    const isEnrolled = studentData?.courses?.some(c => c.courseId === course.courseId);
    
    return matchesSearch && matchesFilter && !isEnrolled;
  });

  const handleContinueLearning = (course) => {
    navigate(`/student/course/${course.courseId}`);
  };

  // Calculate real progress from backend data
  const calculateProgress = (course) => {
    const progress = courseProgress[course.courseId];
    if (!progress) return 0;
    
    // Calculate based on completed items vs total items
    let completed = 0;
    let total = 0;
    
    // Count labs
    if (progress.labProgress?.labs) {
      total += progress.totalLabs || 0;
      completed += progress.labProgress.labs.filter(l => l.isCompleted).length;
    }
    
    // Count quizzes
    if (progress.quizProgress?.quizzes) {
      total += progress.totalQuizzes || 0;
      completed += progress.quizProgress.quizzes.length;
    }
    
    // Count exams
    if (progress.examProgress?.exams) {
      total += progress.totalExams || 0;
      completed += progress.examProgress.exams.length;
    }
    
    if (total === 0) return 0;
    return Math.round((completed / total) * 100);
  };

  // Check if payment is eligible for refund (within 30 days)
  const isRefundEligible = (paymentDate) => {
      const thirtyDaysAgo = new Date();
      thirtyDaysAgo.setDate(thirtyDaysAgo.getDate() - 30);
      return new Date(paymentDate) > thirtyDaysAgo;
  };

  // Open refund modal
  const handleOpenRefundModal = (payment) => {
      // Check if eligible
      if (!isRefundEligible(payment.transactionDate)) {
          showNotification('Refund period has expired (30 days)', 'error');
          return;
      }

      setSelectedPaymentForRefund(payment);
      setRefundReason('');
      setShowRefundModal(true);
  };

  // Submit refund request
  const handleSubmitRefund = async () => {
      if (!refundReason.trim()) {
          showNotification('Please provide a reason for refund', 'error');
          return;
      }

      if (!selectedPaymentForRefund) {
          showNotification('No payment selected', 'error');
          return;
      }

      setIsRefundProcessing(true);
      try {
          const response = await refundPayment(
              authData.id, 
              selectedPaymentForRefund.paymentId,
              refundReason
          );

          if (response.success) {
              showNotification('Refund request submitted for admin approval', 'success');
              setShowRefundModal(false);
              setSelectedPaymentForRefund(null);
              setRefundReason('');
              
              // Refresh payment history
              loadPaymentHistory();
          } else {
              showNotification(response.message || 'Refund request failed', 'error');
          }
      } catch (error) {
          console.error('Refund error:', error);
          showNotification('Failed to submit refund request', 'error');
      } finally {
          setIsRefundProcessing(false);
      }
  };

  // Cancel refund modal
  const handleCancelRefund = () => {
      setShowRefundModal(false);
      setSelectedPaymentForRefund(null);
      setRefundReason('');
  };

  // Cancel subscription
  const handleCancelSubscription = async (stripeSubscriptionId) => {
      if (!window.confirm('Are you sure you want to cancel this subscription?')) {
          return;
      }

      try {
          const response = await cancelSubscription(authData.id, stripeSubscriptionId);

          if (response.success) {
              showNotification('Subscription cancelled successfully', 'success');
              // Refresh subscriptions
              loadSubscriptions();
          } else {
              showNotification(response.message || 'Failed to cancel subscription', 'error');
          }
      } catch (error) {
          console.error('Cancel subscription error:', error);
          showNotification('Failed to cancel subscription', 'error');
      }
  };

  // For pending one-time payments — create new checkout session
  const handleRetryPayment = async (payment) => {
      try {
          const response = await createStripeCheckout(authData.id, {
              successUrl: `${window.location.origin}/payment-success`,
              cancelUrl: `${window.location.origin}/student-dashboard`
          });
          if (response.success && response.data?.url) {
              window.location.href = response.data.url;
          }
      } catch (error) {
          showNotification('Failed to retry payment', 'error');
      }
  };

  // For past_due subscriptions — redirect to Stripe invoice page
  const handleRetrySubscriptionPayment = async (stripeSubscriptionId) => {
      try {
          const response = await getSubscriptionInvoiceUrl(authData.id, stripeSubscriptionId);
          if (response.success && response.data?.url) {
              window.open(response.data.url, '_blank');
          }
      } catch (error) {
          showNotification('Failed to get payment page', 'error');
      }
  };

  // Load payment history
  const loadPaymentHistory = async () => {
      try {
          const response = await getPaymentHistory(authData.id);
          if (response.success) {
              setPaymentHistory(response.data);
          }
      } catch (error) {
          console.error('Failed to load payment history:', error);
      }
  };

  // Load subscriptions
  const loadSubscriptions = async () => {
      try {
          const response = await getSubscriptions(authData.id);
          if (response.success) {
              setSubscriptions(response.data);
          }
      } catch (error) {
          console.error('Failed to load subscriptions:', error);
      }
  };

  // Calculate pending submissions
  const getPendingSubmissions = () => {
    let pending = 0;
    myCourses.forEach(course => {
      const progress = courseProgress[course.courseId];
      if (progress) {
        // Count pending labs
        pending += progress.labProgress?.labs?.filter(l => l.status === 'submitted').length || 0;
        // Count pending quizzes
        pending += progress.quizProgress?.quizzes?.filter(q => 
          q.attempts?.some(a => a.status === 'submitted')
        ).length || 0;
        // Count pending exams
        pending += progress.examProgress?.exams?.filter(e => 
          e.attempts?.some(a => a.status === 'submitted')
        ).length || 0;
        // Count pending capstones
        pending += progress.capstoneProjects?.capstones?.filter(c => 
          c.submissions?.some(s => s.status === 'submitted')
        ).length || 0;
      }
    });
    return pending;
  };


  return (
    <div className="page-container">
      {/* Header */}
      <div className="page-header">
        <ProfileHeader data={authData} activePage="studentdashboard" />
        <div className="headerMiddle">
          <img src="/images/prof_ix.png" alt="Logo" className="header-logo" />
        </div>
        <SharedHeader 
          notificationCount={(unreadCount || 0) + (unreadAnnouncementCount || 0)}
          icons={['envelope']} 
        />
      </div>

      {/* Toolbar */}
      <div className="page-toolbar">
        <BackButton 
            to="-1"
            activeTab=""
            label="←"
            bgColor="#6b7280"
        />
        <span className="page-toolbar-title-wrap">
          <span className="page-toolbar-title">👨‍🏫 Student Dashboard</span>
          <span className="page-toolbar-subtitle">Welcome, {studentData?.name || authData?.name}</span>
        </span>
      </div>

      {/* Tab Navigation */}
      <div className="page-top-tabs">
        <button
          className={`tab-button ${activeTab === 'overview' ? 'active-tab' : ''}`}
          onClick={() => setActiveTab('overview')}
        >
          📊 Overview
        </button>

        <button
          className={`tab-button ${activeTab === 'my-courses' ? 'active-tab' : ''}`}
          onClick={() => setActiveTab('my-courses')}
        >
          📚 My Courses ({myCourses.length})
        </button>

        <button
            className={`tab-button ${activeTab === 'billing' ? 'active-tab' : ''}`}
            onClick={() => setActiveTab('billing')}
        >
            💳 Billing
        </button>

      </div>

      {/* Error Display */}
      {error && (
        <div className="search-container">
          <div style={{ 
            padding: '15px', 
            background: '#fee', 
            color: '#c33', 
            borderRadius: '8px',
            textAlign: 'center'
          }}>
            ⚠️ {error}
          </div>
        </div>
      )}

      {/* Content Area */}
      <div className="page-content">
        {isLoading ? (
          <div className="loading-container">
            <div className="loader"></div>
            <p>Loading your dashboard...</p>
          </div>
        ) : (
          <>
            {/* Overview Tab */}
            {activeTab === 'overview' && (
              <div className="page-inner-wrap">
                
                {/* Statistics Cards */}
                <div className="courses-stat-grid">
                  <div className="stat-card">
                    <div className="stat-info">
                      <div className="stat-icon">📚</div>
                      <h3>{myCourses.length}</h3>
                    </div>
                    <p>Enrolled Courses</p>
                  </div>
                  <div className="stat-card">
                    <div className="stat-info">
                      <div className="stat-icon">✅</div>
                      <h3>{myCourses.filter(c => calculateProgress(c) === 100).length}</h3>
                    </div>
                    <p>Completed Courses</p>
                  </div>
                  <div className="stat-card">
                    <div className="stat-info">
                      <div className="stat-icon">⏳</div>
                      <h3>{getPendingSubmissions()}</h3>
                    </div>
                    <p>Pending Reviews</p>
                  </div>
                  <div className="stat-card">
                    <div className="stat-info">
                      <div className="stat-icon">📊</div>
                      <h3>
                        {/* {Object.values(courseGrades).length > 0 
                          ? (Object.values(courseGrades).reduce((sum, g) => sum + g.finalGrade, 0) / 
                              Object.values(courseGrades).length).toFixed(1) + '%'
                          : 'N/A'} */}
                        {Object.values(courseGrades).length > 0 
                          ? (Object.values(courseGrades).reduce((sum, g) => sum + (g?.finalGrade ?? 0), 0) / 
                            Object.values(courseGrades).length).toFixed(1) + '%'
                          : 'N/A'}
                      </h3>
                    </div>
                    <p>Average Grade</p>
                  </div>
                </div>

                {/* Quick Course Access */}
                <div className="nb-inner-wrap" style={{color: 'white'}}>
                  <div className="nb-header">
                    <h3>📚 My Courses - Quick Access</h3>
                  </div>
                  {myCourses.length > 0 && (
                    <div className="nb-inner-wrap" style={{width: '90%', marginBlock: '20px', display: 'grid', gap: '10px'}}>
                      {myCourses.slice(0, 5).map((course) => {
                        const progress = calculateProgress(course);
                        const grade = courseGrades[course.courseId];
                        console.log("quick access to course1: ", course, course.status);
                        if (course.status !== 'active') return null;
                        return (
                          <div key={course.courseId} className="activity-item">
                            <span className="activity-icon">📚</span>
                            <div className="activity-details" style={{ flex: 1 }}>
                              <p><strong>{course.title}</strong></p>
                              <div style={{ display: 'flex', gap: '15px', marginTop: '5px' }}>
                                <span style={{ fontSize: '13px', color: '#666' }}>
                                  Progress: {progress}%
                                </span>
                                {grade && (
                                  <span style={{ fontSize: '13px', color: '#10b981', fontWeight: 600 }}>
                                    Grade: {grade?.finalGrade.toFixed(1)}% ({grade.letterGrade})
                                  </span>
                                )}
                              </div>
                            </div>
                            <CustomButton 
                              buttonTitle="Continue"
                              action={() => handleContinueLearning(course)}
                              bgColor="#667eea"
                            />
                          </div>
                        );
                      })}
                      {myCourses.length > 5 && (
                        <div style={{ textAlign: 'center', marginTop: '15px' }}>
                          <CustomButton 
                            buttonTitle="View All Courses"
                            action={() => setActiveTab('my-courses')}
                            bgColor="#3b82f6"
                          />
                        </div>
                      )}
                    </div>
                  )}
                </div>

                {/* Recent Activity */}
                <div className="nb-inner-wrap" style={{color: 'white'}}>
                  <div className="nb-header">
                    <h3>📋 Recent Activity</h3>
                    <button 
                      onClick={loadRecentActivities}
                      style={{
                        padding: '8px 16px',
                        background: '#3b82f6',
                        color: 'white',
                        border: 'none',
                        borderRadius: '6px',
                        cursor: 'pointer',
                        fontSize: '14px'
                      }}
                    >
                      🔄 Refresh
                    </button>
                  </div>
                  
                  {activitiesLoading ? (
                    <div style={{ textAlign: 'center', padding: '20px' }}>
                      <div className="loader"></div>
                      <p>Loading activities...</p>
                    </div>
                  ) : recentActivities.length === 0 ? (
                    <div style={{ textAlign: 'center', padding: '30px', color: '#666' }}>
                      <p>No recent activities found</p>
                    </div>
                  ) : (
                    <div className="nb-inner-wrap" style={{width: '90%', marginBlock: '20px', display: 'grid', gap: '10px'}}>
                      {recentActivities.map((activity, index) => (
                        <div key={index} className="activity-item">
                          <span className="activity-icon">{activity.icon}</span>
                          <div className="activity-details">
                            <p><strong>{activity.title}:</strong> {activity.description}</p>
                            <span className="activity-time">{activity.timeAgo}</span>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </div>

              </div>
            )}

            {/* My Courses Tab */}
            {activeTab === 'my-courses' && (
              <div className="nb-inner-wrap" style={{color: 'white'}}>
                <div className="nb-header">
                  <h3>📚 My Courses</h3>
                </div>
                {/* Detailed Progress */}
                <div className="nb-inner-wrap" style={{width: '90%', marginBlock: '20px', display: 'grid', gap: '10px'}}>
                  {myCourses.length > 0 ? (
                    <div className="activity-list">
                      {myCourses.slice(0, 5).map((course) => {
                        const progress = calculateProgress(course);
                        const grade = courseGrades[course.courseId];
                        if ((course.status !== 'active') && (course.status !== 'completed') && (course.status !== 'failed')) return null;
                        return (
                          <div key={course.courseId} className="activity-item">
                            <span className="activity-icon">📚</span>
                            <div className="activity-details" style={{ flex: 1 }}>
                              <p><strong>{course.title}</strong></p>
                              <p className="expertise">
                                {course.category || 'General'} •
                                {` ${course.level}` || 'All Levels'} • 
                                {course.credits ? ` ${course.credits} Credits` : ' Self-paced'}
                              </p>
                              <div style={{ display: 'flex', gap: '15px', marginTop: '5px' }}>
                                <span style={{ fontSize: '13px', color: '#666' }}>
                                  Progress: {progress}%
                                </span>
                                {grade && (
                                  <span style={{ fontSize: '13px', color: '#10b981', fontWeight: 600 }}>
                                    Grade: {grade?.finalGrade.toFixed(1)}% ({grade.letterGrade})
                                  </span>
                                )}
                              </div>
                            </div>
                            <CustomButton 
                              buttonTitle="Continue"
                              action={() => handleContinueLearning(course)}
                              bgColor="#667eea"
                            />
                          </div>
                        );
                      })}
                      {myCourses.length > 5 && (
                        <div style={{ textAlign: 'center', marginTop: '15px' }}>
                          <CustomButton 
                            buttonTitle="View All Courses"
                            action={() => setActiveTab('my-courses')}
                            bgColor="#3b82f6"
                          />
                        </div>
                      )}
                    </div>
                  ) : (
                    <div className="no-items">
                      <p>No courses enrolled yet 📚</p>
                      <CustomButton 
                        buttonTitle="Explore Courses"
                        action={() => setActiveTab('explore')}
                        bgColor="#667eea"
                      />
                    </div>
                  )}
                </div>
              </div>
            )}

            {/* Billing tab - redesigned */}
            {activeTab === 'billing' && (
              <div className="nb-inner-wrap" style={{color: 'white'}}>
                {/* Page header */}
                <div className="billing-page-header">
                    <div className="billing-page-icon">
                        <svg viewBox="0 0 24 24" fill="none" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round">
                            <rect x="1" y="4" width="22" height="16" rx="3"/><line x1="1" y1="10" x2="23" y2="10"/>
                        </svg>
                    </div>
                    <div className="nb-header" style={{flexDirection: 'column', alignItems: 'flex-start'}}>
                        <h3>Billing &amp; Payments</h3>
                        <p>Manage your subscriptions, invoices, and refund requests</p>
                    </div>
                </div>

                {/* Refund policy banner — unchanged */}
                <div className="billing-policy-banner">
                    <div className="billing-policy-banner-header">
                        <svg viewBox="0 0 24 24" fill="none" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                            <circle cx="12" cy="12" r="10"/><line x1="12" y1="16" x2="12" y2="12"/><line x1="12" y1="8" x2="12.01" y2="8"/>
                        </svg>
                        <span>Refund Policy</span>
                    </div>
                    <ul className="billing-policy-list">
                        <li><span className="billing-policy-arrow">→</span>Refund requests must be submitted within 30 days of the original payment date.</li>
                        <li><span className="billing-policy-arrow">→</span>One-time course purchases are eligible for a full refund if less than 20% of the course has been completed.</li>
                        <li><span className="billing-policy-arrow">→</span>Monthly subscriptions can be cancelled anytime; refunds are prorated for unused days in the current billing cycle.</li>
                        <li><span className="billing-policy-arrow">→</span>Refunds are issued to the original payment method and typically appear within 5–10 business days.</li>
                        <li><span className="billing-policy-arrow">→</span>For questions, contact <a href="mailto:billing@futureix.com">billing@futureix.com</a></li>
                    </ul>
                </div>

                {/* ── ONE-TIME PAYMENTS ── */}
                {(() => {
                    const oneTimePayments = paymentHistory.filter(p => !p.isInstallment);
                    if (oneTimePayments.length === 0) return null;
                    return (
                        <div className="billing-section-block">
                            <div className="billing-section-label">One-Time Purchases</div>
                            {oneTimePayments.map((payment) => {
                                const eligible = isRefundEligible(payment.transactionDate);
                                const canRefund = (payment.status === 'completed' || payment.status === 'pending') && eligible;
                                const expiredWindow = (payment.status === 'completed' || payment.status === 'pending') && !eligible;
                                return (
                                    <div key={payment.paymentId} className="billing-payment-card">
                                        <div className="billing-payment-top">
                                            <div className="billing-payment-left">
                                                <div className="billing-payment-avatar">
                                                    <svg viewBox="0 0 24 24" fill="none" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round">
                                                        <rect x="1" y="4" width="22" height="16" rx="3"/><line x1="1" y1="10" x2="23" y2="10"/>
                                                    </svg>
                                                </div>
                                                <div>
                                                    {payment.cartItems?.length > 0 && (
                                                        <div>
                                                            {payment.cartItems.map((item, idx) => (
                                                                <span key={idx} className="billing-course-pill">{item.courseName}</span>
                                                            ))}
                                                        </div>
                                                    )}
                                                    <div className="billing-payment-date">{formatShortDate(payment.transactionDate)}</div>
                                                </div>
                                            </div>
                                            <div className="billing-payment-right">
                                                <div className="billing-payment-amount">{payment.currency} {payment.amount.toFixed(2)}</div>
                                                <span className={`billing-status-badge ${payment.status}`}>
                                                    {payment.status.replace('_', ' ')}
                                                </span>
                                            </div>
                                        </div>
                                        
                                        <div className="billing-payment-actions">
                                            {payment.stripePaymentIntentId && (
                                                <span className="billing-txn-id">{payment.stripePaymentIntentId.substring(0, 24)}...</span>
                                            )}
                                            {canRefund && (
                                                <button className="billing-btn-refund" onClick={() => handleOpenRefundModal(payment)}>
                                                    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                                        <polyline points="1 4 1 10 7 10"/><path d="M3.51 15a9 9 0 1 0 .49-3.51"/>
                                                    </svg>
                                                    Request Refund
                                                </button>
                                            )}
                                            {expiredWindow && (
                                                <span className="billing-badge-not-eligible">
                                                    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                                        <circle cx="12" cy="12" r="10"/><line x1="4.93" y1="4.93" x2="19.07" y2="19.07"/>
                                                    </svg>
                                                    Not eligible for refund
                                                </span>
                                            )}
                                            {payment.status === 'refund_pending' && (
                                                <span className="billing-badge-pending-refund">
                                                    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                                        <circle cx="12" cy="12" r="10"/><polyline points="12 6 12 12 16 14"/>
                                                    </svg>
                                                    Processing refund
                                                </span>
                                            )}
                                            {payment.status === 'refunded' && (
                                                <span className="billing-badge-refunded">
                                                    <svg viewBox="0 0 24 24" fill="none" strokeWidth="2" strokeLinecap="round">
                                                        <polyline points="20 6 9 17 4 12"/>
                                                    </svg>
                                                    Refund issued
                                                </span>
                                            )}
                                            {(payment.status === 'completed' || payment.status === 'refunded') && (
                                                <>
                                                    <button className="billing-btn-invoice" onClick={() => { setSelectedPayment(payment); setShowPaymentModal(true); }}>
                                                        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round">
                                                            <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/><polyline points="14 2 14 8 20 8"/>
                                                        </svg>
                                                        Invoice
                                                    </button>
                                                    {payment.stripeInvoiceUrl && (
                                                        <button onClick={() => window.open(payment.stripeInvoiceUrl, '_blank')}>Stripe Invoice</button>
                                                    )}
                                                    {payment.stripeInvoicePdfUrl && (
                                                        <button onClick={() => window.open(payment.stripeInvoicePdfUrl, '_blank')}>Download Invoice</button>
                                                    )}
                                                </>
                                            )}
                                            {payment.status === 'pending' && (
                                                <button
                                                    className="billing-btn-invoice"
                                                    onClick={() => handleRetryPayment(payment)}
                                                    style={{ background: '#e67e22', color: '#fff', border: 'none' }}
                                                >
                                                    ⏳ Complete Payment
                                                </button>
                                            )}
                                        </div>
                                    </div>
                                );
                            })}
                        </div>
                    );
                })()}

                {/* ── INSTALLMENT SUBSCRIPTIONS ── */}
                {(() => {
                    if (subscriptions.length === 0) return null;
                    return (
                        <div className="billing-section-block">
                            <div className="billing-section-label">Installment Plans</div>
                            {subscriptions.map((sub) => {
                                const isActive = sub.status === 'active';
                                const isExpanded = expandedSubs.has(sub.subscriptionId);

                                // All monthly payments for this subscription, oldest first
                                const subPayments = paymentHistory
                                    .filter(p => p.isInstallment && p.stripeSubscriptionId === sub.stripeSubscriptionId)
                                    .sort((a, b) => new Date(a.transactionDate) - new Date(b.transactionDate));

                                const planLabels = {
                                    monthly_3: '3 Monthly Payments',
                                    monthly_6: '6 Monthly Payments',
                                    monthly_12: '12 Monthly Payments'
                                };
                                const totalMonths = parseInt(sub.planType.split('_')[1]) || 0;
                                const paidCount = subPayments.length;

                                return (
                                    <div key={sub.subscriptionId} className="billing-sub-card billing-sub-card--expandable">
                                        {/* Subscription header row */}
                                        <div
                                            className="billing-sub-header-row"
                                            onClick={() => toggleSubExpanded(sub.subscriptionId)}
                                            style={{ cursor: 'pointer' }}
                                        >
                                          <div className="billing-sub-left">
                                            <div className={`billing-sub-dot ${isActive ? 'active' : 'inactive'}`} />
                                              <div>
                                                <span className="billing-course-pill">{sub.courseName}</span>
                                                <div className="billing-sub-meta">
                                                    {planLabels[sub.planType] || sub.planType}
                                                    {' · '}{paidCount} of {totalMonths} payments made
                                                    {sub.lastPaymentAt && ` · Last payment ${formatShortDate(sub.lastPaymentAt)}`}
                                                </div>

                                                {/* Access expiry info */}
                                                {sub.accessExpiresAt && (
                                                    <div style={{ fontSize: '12px', color: '#2d7a3a', marginTop: 4 }}>
                                                        📅 Access until: {formatShortDate(sub.accessExpiresAt)}
                                                    </div>
                                                )}

                                                {/* Grace period warning */}
                                                {sub.gracePeriodEndsAt && sub.status === 'past_due' && (
                                                    <div style={{ fontSize: '12px', color: '#c0392b', marginTop: 4 }}>
                                                        ⚠️ Update payment by: {formatShortDate(sub.gracePeriodEndsAt)}
                                                    </div>
                                                )}

                                                {/* Plan completed */}
                                                {sub.completedAt && (
                                                    <div style={{ fontSize: '12px', color: '#2f62ee', marginTop: 4 }}>
                                                        ✅ Plan completed: {formatShortDate(sub.completedAt)}
                                                    </div>
                                                )}
                                              </div>
                                            </div>
                                            <div className="billing-sub-right">
                                                <div className="billing-sub-amount">
                                                    <div className="billing-sub-amount-value">{sub.currency} {sub.amount.toFixed(2)}</div>
                                                    <div className="billing-sub-amount-period">/month</div>
                                                </div>
                                                <span className={`billing-status-badge ${sub.status}`}>{sub.status}</span>
                                                {sub.status === 'past_due' && (
                                                    <button
                                                        className="billing-btn-invoice"
                                                        onClick={() => handleRetrySubscriptionPayment(sub.stripeSubscriptionId)}
                                                        style={{ background: '#e67e22', color: '#fff', border: 'none' }}
                                                    >
                                                        ⚠️ Update Payment
                                                    </button>
                                                )}
                                                {isActive && (
                                                    <button
                                                        className="billing-btn-cancel-sub"
                                                        onClick={(e) => { e.stopPropagation(); handleCancelSubscription(sub.stripeSubscriptionId); }}
                                                    >
                                                        Cancel
                                                    </button>
                                                )}
                                                {/* Expand chevron */}
                                                <svg
                                                    viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"
                                                    strokeLinecap="round" strokeLinejoin="round"
                                                    style={{ width: 18, height: 18, marginLeft: 8, marginRight: 40, transition: 'transform 0.2s', transform: isExpanded ? 'rotate(180deg)' : 'rotate(0deg)', flexShrink: 0 }}
                                                >
                                                    <polyline points="6 9 12 15 18 9"/>
                                                </svg>
                                            </div>
                                          </div>

                                        {/* Expanded monthly payments */}
                                        {isExpanded && (
                                            <div className="billing-sub-payments">
                                                <div className="billing-payment-divider" />
                                                {subPayments.length === 0 ? (
                                                    <div className="billing-empty" style={{ padding: '12px 0' }}>No payments recorded yet.</div>
                                                ) : (
                                                    subPayments.map((payment) => {
                                                        const eligible = isRefundEligible(payment.transactionDate);
                                                        const canRefund = payment.status === 'completed' && eligible;
                                                        const expiredWindow = payment.status === 'completed' && !eligible;
                                                        return (
                                                            <div key={payment.paymentId} className="billing-installment-row">
                                                                <div className="billing-installment-left">
                                                                    <div className="billing-installment-num">
                                                                        #{payment.installmentNumber ?? '—'}
                                                                    </div>
                                                                    <div>
                                                                        <div className="billing-payment-title" style={{ fontSize: '13px' }}>
                                                                            Installment {payment.installmentNumber} of {totalMonths}
                                                                        </div>
                                                                        <div className="billing-payment-date">{formatShortDate(payment.transactionDate)}</div>
                                                                        {payment.remainingBalance !== undefined && (
                                                                            <div style={{ fontSize: '12px', color: '#888', marginTop: 2 }}>
                                                                                Remaining: {payment.currency} {payment.remainingBalance.toFixed(2)}
                                                                            </div>
                                                                        )}
                                                                    </div>
                                                                </div>
                                                                
                                                                <div className="billing-installment-right">
                                                                  {/* Amount + status on same line */}
                                                                  <div className="billing-installment-top">
                                                                      <div className="billing-payment-amount" style={{ fontSize: '14px' }}>
                                                                          {payment.currency} {payment.amount.toFixed(2)}
                                                                      </div>
                                                                      <span className={`billing-status-badge ${payment.status}`}>
                                                                          {payment.status.replace('_', ' ')}
                                                                      </span>
                                                                  </div>
                                                                  {/* Actions below */}
                                                                  <div className="billing-installment-actions">
                                                                      {canRefund && (
                                                                          <button className="billing-btn-refund" onClick={() => handleOpenRefundModal(payment)}>
                                                                              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                                                                  <polyline points="1 4 1 10 7 10"/><path d="M3.51 15a9 9 0 1 0 .49-3.51"/>
                                                                              </svg>
                                                                              Refund
                                                                          </button>
                                                                      )}
                                                                      {expiredWindow && (
                                                                          <span className="billing-badge-not-eligible" style={{ fontSize: '11px' }}>Not eligible</span>
                                                                      )}
                                                                      {payment.status === 'refund_pending' && (
                                                                          <span className="billing-badge-pending-refund" style={{ fontSize: '11px' }}>Processing</span>
                                                                      )}
                                                                      {payment.status === 'refunded' && (
                                                                          <span className="billing-badge-refunded" style={{ fontSize: '11px' }}>Refunded</span>
                                                                      )}
                                                                      {(payment.status === 'completed' || payment.status === 'refunded' || payment.status === 'refund_pending') && (
                                                                          <button className="billing-btn-invoice" onClick={() => { setSelectedPayment(payment); setShowPaymentModal(true); }}>
                                                                              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round">
                                                                                  <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/><polyline points="14 2 14 8 20 8"/>
                                                                              </svg>
                                                                              Invoice
                                                                          </button>
                                                                      )}
                                                                      {payment.stripeInvoicePdfUrl && (
                                                                          <button onClick={() => window.open(payment.stripeInvoicePdfUrl, '_blank')}>PDF</button>
                                                                      )}
                                                                  </div>
                                                              </div>
                                                            </div>
                                                        );
                                                    })
                                                )}
                                            </div>
                                        )}
                                    </div>
                                );
                            })}
                        </div>
                    );
                })()}

                {/* Empty state */}
                {paymentHistory.length === 0 && subscriptions.length === 0 && (
                    <div className="billing-section-block">
                        <div className="billing-empty">No payment history yet.</div>
                    </div>
                )}

              </div>
            )}
          </>
        )}
      </div>

      {/* Refund Request Modal */}
      {showRefundModal && selectedPaymentForRefund && (
          <div className="modal-overlay" onClick={handleCancelRefund}>
              <div className="modal-content-top refund-modal" onClick={(e) => e.stopPropagation()}>
                  <div className="modal-header-top">
                      <h3>💰 Request Refund</h3>
                      <button onClick={handleCancelRefund} className="close-button">✕</button>
                  </div>

                  <div className="modal-body">
                      <h3>Payment Details</h3>
                      <div className="am-info-grid">
                          
                          <div className="am-badge" style={{background: '#f3eb7e'}}>
                            <span className="am-badge-label" style={{color: '#21201f'}}>Amount:</span>
                            <span className="am-badge-value" style={{ fontSize: '13px' }}>{selectedPaymentForRefund.currency} {selectedPaymentForRefund.amount.toFixed(2)}</span>
                          </div>
                          
                          <div className="am-badge" style={{background: '#7ee5f3'}}>
                            <span className="am-badge-label" style={{color: '#21201f'}}>Payment Status:</span>
                            <span className="am-badge-value" style={{ fontSize: '13px' }}>{selectedPaymentForRefund.status}</span>
                          </div>

                          <div className="am-badge" style={{background: '#7e9df3'}}>
                            <span className="am-badge-label" style={{color: '#21201f'}}>Date</span>
                            <span className="am-badge-value" style={{ fontSize: '13px' }}>{formatShortDate(selectedPaymentForRefund.transactionDate)}</span>
                          </div>
                      </div>

                      <div className="refund-summary">
                          {selectedPaymentForRefund.cartItems && (
                              <div className="refund-courses">
                                  <h3>Courses:</h3>
                                  <div className="am-badge" style={{borderColor: '#9495f1', gap:'7px'}}>
                                      {selectedPaymentForRefund.cartItems.map((item, idx) => (
                                          // <li key={idx}>{item.courseName}</li>
                                          <span className="am-badge-value" style={{color: '#2f62ee'}}>{item.courseName}</span>
                                      ))}
                                  </div>
                              </div>
                          )}
                      </div>

                      <div className="refund-form">
                          <label htmlFor="refund-reason">
                              Reason for Refund <span className="required">*</span>
                          </label>
                          <textarea
                              id="refund-reason"
                              value={refundReason}
                              onChange={(e) => setRefundReason(e.target.value)}
                              placeholder="Please explain why you're requesting a refund..."
                              rows="4"
                              className="refund-reason-input"
                          />
                      </div>

                      <div className="refund-policy-notice">
                          <p>⚠️ <strong>Refund Policy:</strong></p>
                          <ul>
                              <li>Refunds must be requested within 30 days of purchase</li>
                              <li>Your request will be reviewed by our admin team</li>
                              <li>You'll receive an email once your request is processed</li>
                              <li>If approved, refund will be processed within 5-7 business days</li>
                          </ul>
                      </div>

                      <div className="modal-actions">
                          <button
                              onClick={handleCancelRefund}
                              className="btn-cancel"
                              disabled={isRefundProcessing}
                          >
                              Cancel
                          </button>
                          <button
                              onClick={handleSubmitRefund}
                              className="btn-submit-refund"
                              disabled={!refundReason.trim() || isRefundProcessing || selectedPaymentForRefund.status === 'pending'}
                          >
                              {isRefundProcessing ? '⏳ Submitting...' : '✅ Submit Request'}
                          </button>
                      </div>
                  </div>
              </div>
          </div>
      )}

      {/* Invoice Modal */}
      {showPaymentModal && selectedPayment && (
        <div className="modal-overlay" onClick={() => setShowPaymentModal(false)}>
          <div className="modal-content-top invoice-modal sb-invoice-modal" onClick={(e) => e.stopPropagation()}>
            
            {/* Modal Header Bar */}
            <div className="modal-header-top sb-invoice-header">
              <h2 className="sb-invoice-header-title">Payment Invoice</h2>
              <button onClick={() => setShowPaymentModal(false)} className="close-button">✕</button>
            </div>

            {/* Printable Invoice Body */}
            <div className="modal-body sb-invoice-body" id="invoice-print-area">

              {/* TOP ROW */}
              <div className="sb-invoice-top-row">
                <h1 className="sb-invoice-title">Invoice</h1>
                <img src="/images/prof_ix.png" alt="Prof-iX" className="sb-invoice-logo" />
              </div>

              {/* Invoice Meta */}
              <div className="sb-invoice-meta">
                <div><strong>Invoice number</strong>&nbsp;&nbsp;&nbsp;{selectedPayment.paymentId}</div>
                <div><strong>Date of issue</strong>&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;{new Date(selectedPayment.transactionDate).toLocaleDateString('en-CA', { year: 'numeric', month: 'long', day: 'numeric' })}</div>
                <div><strong>Date due</strong>&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;{new Date(selectedPayment.transactionDate).toLocaleDateString('en-CA', { year: 'numeric', month: 'long', day: 'numeric' })}</div>
              </div>


              {/* From / Bill To */}
              <div className="sb-invoice-parties">
                <div className="sb-invoice-from">
                  <div className="sb-party-name">Prof-iX STEM Learning Platform</div>
                  <div className="sb-party-details">
                    <div>Organizer: FutureiX Inc.</div>
                    <div>info@futureix.ca</div>
                    <div>Canada</div>
                  </div>
                </div>
                <div className="sb-invoice-to">
                  <div className="sb-party-name">Bill to</div>
                  <div className="sb-party-details">
                    <div>{authData?.name}</div>
                    <div>{authData?.email}</div>
                  </div>
                </div>
              </div>

              {/* Amount Due Banner */}
              <div className="sb-amount-banner">
                <div className="sb-amount-due">
                  {selectedPayment.currency} {selectedPayment.amount.toFixed(2)} due {new Date(selectedPayment.transactionDate).toLocaleDateString('en-CA', { year: 'numeric', month: 'long', day: 'numeric' })}
                </div>
                {selectedPayment.stripeInvoiceUrl && (
                  <a href={selectedPayment.stripeInvoiceUrl} target="_blank" rel="noreferrer" className="sb-view-online">
                    View online
                  </a>
                )}
              </div>

              {/* Line Items Table */}
              <table className="sb-invoice-table">
                <thead>
                  <tr className="sb-table-header-row">
                    <th className="sb-th-left">Description</th>
                    <th className="sb-th-center">Qty</th>
                    <th className="sb-th-right">Unit price</th>
                    <th className="sb-th-right">Amount</th>
                  </tr>
                </thead>
                <tbody>
                  {selectedPayment.cartItems?.map((item, idx) => (
                    <tr key={idx} className="sb-table-row">
                      <td className="sb-td-desc">
                        <div className="sb-course-name">
                          {item.courseName} ({selectedPayment.courseIds?.[0]})
                        </div>
                        {selectedPayment.isInstallment && (
                          <div className="sb-installment-label">
                            {selectedPayment.installmentPlan?.replace('_', ' ')} plan — Installment #{selectedPayment.installmentNumber}
                          </div>
                        )}
                      </td>
                      <td className="sb-td-center">1</td>
                      <td className="sb-td-right">
                        {selectedPayment.currency} {selectedPayment.isInstallment
                          ? selectedPayment.installmentAmount?.toFixed(2)
                          : item.coursePrice?.toFixed(2)}
                      </td>
                      <td className="sb-td-right">
                        {selectedPayment.currency} {selectedPayment.amount.toFixed(2)}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>

              {/* Totals */}
              <div className="sb-totals">

                {/* Coupon info */}
                {selectedPayment.isCoupon && (
                  <>
                    <div className="sb-total-row">
                      <span className="sb-total-label">Original price</span>
                      <span>{selectedPayment.currency} {selectedPayment.originalCoursePrice?.toFixed(2)}</span>
                    </div>
                    <div className="sb-total-row">
                      <span className="sb-total-label sb-coupon-label">✓ Coupon applied</span>
                      <span className="sb-coupon-value">
                        -{selectedPayment.currency} {((selectedPayment.originalCoursePrice ?? 0) - (selectedPayment.discountedCoursePrice ?? 0)).toFixed(2)}
                      </span>
                    </div>
                    <div className="sb-total-row">
                      <span className="sb-total-label">Price after coupon</span>
                      <span>{selectedPayment.currency} {selectedPayment.discountedCoursePrice?.toFixed(2)}</span>
                    </div>
                  </>
                )}

                <div className="sb-total-row">
                  <span className="sb-total-label">Subtotal</span>
                  <span>{selectedPayment.currency} {selectedPayment.amount.toFixed(2)}</span>
                </div>
                <div className="sb-total-row">
                  <span className="sb-total-label">Tax</span>
                  <span>—</span>
                </div>
                <div className="sb-total-row sb-total-final">
                  <span>Amount paid</span>
                  <span>{selectedPayment.currency} {selectedPayment.amount.toFixed(2)}</span>
                </div>

                {/* Installment remaining */}
                {selectedPayment.isInstallment && (
                  <>
                    <div className="sb-total-row sb-installment-row">
                      <span>Remaining balance</span>
                      <span>{selectedPayment.currency} {selectedPayment.remainingBalance?.toFixed(2)}</span>
                    </div>
                    <div className="sb-total-row sb-installment-row">
                      <span>Installment end date</span>
                      <span>{selectedPayment.installmentEndDate
                        ? new Date(selectedPayment.installmentEndDate).toLocaleDateString('en-CA', { year: 'numeric', month: 'long', day: 'numeric' })
                        : '—'}
                      </span>
                    </div>
                  </>
                )}
              </div>

              {/* Transaction ID */}
              {selectedPayment.stripePaymentIntentId && (
                <div className="sb-transaction-id">
                  Transaction ID: {selectedPayment.stripePaymentIntentId}
                </div>
              )}

              {/* Status */}
              <div className="sb-status-row">
                Status: <span className={`sb-status-badge ${selectedPayment.status === 'completed' ? 'sb-status-completed' : 'sb-status-failed'}`}>
                  {selectedPayment.status}
                </span>
              </div>

              {/* Subscription Details */}
              {selectedPayment.paymentMethod === 'stripe_subscription' && (() => {
                const matchingSubscription = selectedPayment?.stripeSubscriptionId
                  ? studentData?.subscriptions?.find(
                      (s) => s.stripeSubscriptionId === selectedPayment.stripeSubscriptionId
                    )
                  : studentData?.subscriptions?.find(
                      (s) => selectedPayment?.courseIds?.includes(s.courseId)
                    );

                return matchingSubscription ? (
                  <div className="sb-subscription-details">
                    <div className="sb-subscription-details-title">📋 Subscription Details</div>
                    <div className="sb-subscription-grid">
                      <span className="sb-sub-label">Payment Method:</span>
                      <span className="sb-sub-value">Installment Plan</span>

                      <span className="sb-sub-label">Plan:</span>
                      <span className="sb-sub-value">
                        {matchingSubscription.planType === 'monthly_3' ? '3 Monthly Payments' :
                        matchingSubscription.planType === 'monthly_6' ? '6 Monthly Payments' :
                        matchingSubscription.planType === 'monthly_12' ? '12 Monthly Payments' :
                        matchingSubscription.planType}
                      </span>

                      <span className="sb-sub-label">Monthly Installment:</span>
                      <span className="sb-sub-value">
                        {matchingSubscription.currency} {matchingSubscription.amount.toFixed(2)}/month
                      </span>

                      <span className="sb-sub-label">Subscription ID:</span>
                      <span className="sb-sub-value sb-sub-value-small">
                        {matchingSubscription.stripeSubscriptionId}
                      </span>

                      <span className="sb-sub-label">Subscription Status:</span>
                      <span className={`sb-sub-status ${matchingSubscription.status === 'active' ? 'sb-sub-status-active' : 'sb-sub-status-inactive'}`}>
                          {matchingSubscription.status}
                      </span>

                      {matchingSubscription.cancelAt && (
                          <>
                              <span className="sb-sub-label">Plan End Date:</span>
                              <span className="sb-sub-value">
                                  {new Date(matchingSubscription.cancelAt).toLocaleDateString('en-CA', { year: 'numeric', month: 'long', day: 'numeric' })}
                              </span>
                          </>
                      )}

                      {matchingSubscription.accessExpiresAt && (
                          <>
                              <span className="sb-sub-label">Access Until:</span>
                              <span className="sb-sub-value" style={{ color: '#2d7a3a' }}>
                                  {new Date(matchingSubscription.accessExpiresAt).toLocaleDateString('en-CA', { year: 'numeric', month: 'long', day: 'numeric' })}
                              </span>
                          </>
                      )}

                      {matchingSubscription.completedAt && (
                          <>
                              <span className="sb-sub-label">Completed On:</span>
                              <span className="sb-sub-value" style={{ color: '#2f62ee' }}>
                                  {new Date(matchingSubscription.completedAt).toLocaleDateString('en-CA', { year: 'numeric', month: 'long', day: 'numeric' })}
                              </span>
                          </>
                      )}

                      {matchingSubscription.gracePeriodEndsAt && matchingSubscription.status === 'past_due' && (
                          <>
                              <span className="sb-sub-label">⚠️ Payment Due By:</span>
                              <span className="sb-sub-value" style={{ color: '#c0392b' }}>
                                  {new Date(matchingSubscription.gracePeriodEndsAt).toLocaleDateString('en-CA', { year: 'numeric', month: 'long', day: 'numeric' })}
                              </span>
                          </>
                      )}
                    </div>
                  </div>
                ) : null;
              })()}

              {/* Buttons */}
              <div className="sb-invoice-actions">
                <button onClick={() => {
                  const printArea = document.getElementById('invoice-print-area');
                  const printWindow = window.open('', '_blank');
                  printWindow.document.write(`
                    <html>
                      <head>
                        <title>Invoice - ${selectedPayment.paymentId}</title>
                        <style>
                          body { font-family: Arial, sans-serif; padding: 40px; color: #111; }
                          table { width: 100%; border-collapse: collapse; }
                          th { text-align: left; border-bottom: 1px solid #ccc; padding-bottom: 8px; color: #555; }
                          td { padding: 10px 0; border-bottom: 1px solid #eee; }
                        </style>
                      </head>
                      <body>${printArea.innerHTML}</body>
                    </html>
                  `);
                  printWindow.document.close();
                  printWindow.focus();
                  setTimeout(() => { printWindow.print(); printWindow.close(); }, 500);
                }} className="btn-refund">
                  🖨️ Print / Save PDF
                </button>
                {selectedPayment.stripeInvoiceUrl && (
                  <button onClick={() => window.open(selectedPayment.stripeInvoiceUrl, '_blank')} className="btn-refund">
                    🔗 Stripe Invoice
                  </button>
                )}
                {selectedPayment.stripeInvoicePdfUrl && (
                  <button onClick={() => window.open(selectedPayment.stripeInvoicePdfUrl, '_blank')} className="btn-refund">
                    📄 Download PDF
                  </button>
                )}
              </div>

            </div>
          </div>
        </div>
      )}

    </div>
  );
};

export default StudentDashboard;